import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import AirportInfo from "../AirportInfo";
import EmulacaoSeletores from "./EmulacaoSeletores";
import FaixaDeEmulacao from "./FaixaDeEmulacao";
import "./index.css";

export interface NavBarItem {
  iconName: string;
  label: string;
  route?: string;
  onClick?: () => void;
  isHidden?: boolean;
  /**
   * Quando "toggle", o item vira uma flag (checkbox) no menu em vez de um link de navegação.
   * Nesse caso "checked" e "onToggle" são usados no lugar de "route".
   */
  type?: "link" | "toggle";
  checked?: boolean;
  onToggle?: (checked: boolean) => void;
}

interface NavBarProps {
  items: NavBarItem[];
}

interface NavBarContextValue {
  items: NavBarItem[];
  setItems: React.Dispatch<React.SetStateAction<NavBarItem[]>>;
}

const NavBarContext = createContext<NavBarContextValue>({
  items: [],
  setItems: () => {},
});

/**
 * Guarda os itens que a pagina atual quer exibir na barra global.
 * Deve envolver tanto o <Header /> (que renderiza a barra) quanto as
 * rotas (que publicam os itens).
 */
export const NavBarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<NavBarItem[]>([]);
  const value = useMemo(() => ({ items, setItems }), [items]);

  return <NavBarContext.Provider value={value}>{children}</NavBarContext.Provider>;
};

/**
 * A barra em si. Renderizada uma unica vez, no Header, para que o bloco
 * de aeroportos apareca em todas as telas — inclusive nas que nao
 * publicam nenhum link (Home, Report).
 */
export const NavBarOutlet: React.FC = () => {
  const { items } = useContext(NavBarContext);

  return (
    <>
      <div className="nav-bar">
        <div className="container-nav">
          {items
            .filter((item) => !item.isHidden)
            .map((item, index) => (
              <div
                key={index}
                className="nav-link"
                style={{ cursor: "pointer" }}
              >
                <i className={`bi bi-${item.iconName}`} style={{ cursor: "pointer" }}></i>
                {item.type === "toggle" ? (
                  <label
                    style={{
                      alignItems: "center",
                      cursor: "pointer",
                      display: "inline-flex",
                      gap: "5px",
                      marginBottom: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!item.checked}
                      onChange={(e) => item.onToggle?.(e.target.checked)}
                      style={{ cursor: "pointer" }}
                    />
                    <span>{item.label}</span>
                  </label>
                ) : (
                  <NavLink to={item.route ?? "#"} onClick={item.onClick}>
                    <span>{item.label}</span>
                  </NavLink>
                )}
              </div>
            ))}

          <EmulacaoSeletores />

          <div className="giant-airport-strip py-1 px-3">
            <AirportInfo />
          </div>
        </div>
      </div>

      <FaixaDeEmulacao />
    </>
  );
};

/**
 * Publica os itens da pagina na barra global e limpa ao desmontar.
 * Nao renderiza nada no lugar onde e chamado — a barra vive no Header.
 * A assinatura continua a mesma (<NavBar items={navItems} />), entao as
 * paginas nao precisaram mudar.
 */
const NavBar: React.FC<NavBarProps> = ({ items }) => {
  const { setItems } = useContext(NavBarContext);

  // `items` e recriado a cada render das paginas; a chave serializada
  // evita republicar (e re-renderizar a barra) sem que nada tenha mudado.
  const itemsKey = JSON.stringify(
    items.map(({ iconName, label, route, isHidden }) => ({ iconName, label, route, isHidden }))
  );

  useEffect(() => {
    setItems(items);
    return () => setItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, setItems]);

  return null;
};

export default NavBar;
